import { useMemo } from 'react'
import { Box } from '@mui/system'
import { Typography, Button } from '@mui/material'
import { useHistory, useLocation } from 'react-router-dom'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace'
import UseTranslation from '../../../../hooks/UseTranslation'
import AppRoutes from '../../../../services/AppRoutes'
import { resolveSamlErrorDefinition, SAML_ERROR_VARIANT } from './samlErrorConfig'

const SamlErrorPage = () => {
  const { t } = UseTranslation()
  const history = useHistory()
  const location = useLocation()

  const { definition, registeredEmail, idpEmail } = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const raw =
      params.get('errorCode') || params.get('code') || params.get('error') || ''
    const def = resolveSamlErrorDefinition(raw)
    return {
      definition: def,
      registeredEmail: params.get('registeredEmail')?.trim() || '',
      idpEmail: params.get('idpEmail')?.trim() || '',
      rawErrorCode: raw.trim(),
    }
  }, [location.search])

  const { variant, titleKey, subtitleKey, detailKeys } = definition
  const isWarning = variant === SAML_ERROR_VARIANT.WARNING
  const showEmailDiff =
    isWarning && Boolean(registeredEmail && idpEmail)

  const goLogin = () => {
    history.push(AppRoutes.login)
  }

  return (
    <Box sx={styles.page}>
      <Box sx={styles.centerWrap}>
        <Box sx={styles.card}>

          <Box sx={styles.cardBody}>
            <Box sx={styles.iconCluster}>
              <Box
                sx={[
                  styles.iconCircle,
                  isWarning ? styles.iconWarning : styles.iconError,
                ]}
              >
                {isWarning ? (
                  <WarningAmberIcon sx={styles.warningIcon} />
                ) : (
                  <ErrorOutlineIcon sx={styles.errorIcon} />
                )}
              </Box>
            </Box>

            <Typography component="h1" sx={styles.errorTitle}>
              {t?.[titleKey]}
            </Typography>
            <Typography sx={styles.errorSubtitle}>{t?.[subtitleKey]}</Typography>

            {showEmailDiff && (
              <Box sx={styles.emailDiff}>
                <Typography sx={styles.emailDiffLabel}>
                  {t?.SAML_ERROR_EMAIL_COMPARE_LABEL}
                </Typography>
                <Box sx={[styles.emailRow, styles.emailRowActive]}>
                  <Box sx={[styles.emailDot, styles.emailDotActive]} />
                  <Typography component="span" sx={styles.emailMono}>
                    {registeredEmail}
                  </Typography>
                  <Typography component="span" sx={[styles.emailTag, styles.emailTagActive]}>
                    {t?.SAML_ERROR_EMAIL_TAG_REGISTERED}
                  </Typography>
                </Box>
                <Box sx={[styles.emailRow, styles.emailRowMismatch]}>
                  <Box sx={[styles.emailDot, styles.emailDotMismatch]} />
                  <Typography component="span" sx={styles.emailMono}>
                    {idpEmail}
                  </Typography>
                  <Typography component="span" sx={[styles.emailTag, styles.emailTagMismatch]}>
                    {t?.SAML_ERROR_EMAIL_TAG_IDP}
                  </Typography>
                </Box>
              </Box>
            )}

{detailKeys?.length > 0 && (
            <Box
              sx={[
                styles.detailBox,
                isWarning ? styles.detailWarning : styles.detailError,
              ]}
            >
              {detailKeys?.map((key) => (
                <Box key={key} sx={styles.detailRow}>
                  <Box
                    sx={[
                      styles.detailDot,
                      isWarning ? styles.detailDotWarn : styles.detailDotErr,
                    ]}
                  />
                  <Typography component="div" sx={styles.detailText}>
                    {t?.[key]}
                  </Typography>
                </Box>
              ))}
            </Box>
)}
            {/* <Box sx={styles.codeBlock}>
              <Typography component="span" sx={styles.codeMuted}>
                {t?.SAML_ERROR_CODE_LABEL}
              </Typography>
              <Typography component="span" sx={styles.codeValue}>
                {rawErrorCode || t?.SAML_ERROR_CODE_FALLBACK}
              </Typography>
            </Box> */}

            <Box sx={styles.actionRow}>
              <Button
                type="button"
                onClick={goLogin}
                sx={isWarning ? styles.btnPrimary : styles.btnSecondary}
                fullWidth
              >
                <KeyboardBackspaceIcon fontSize="small" />
                {t?.['Back to login']}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

const brandGreen = '#2ecc9a'
const brandGreenDark = '#1faa80'
const brandDark = '#1a2e2a'
const brandText = '#2d3f3b'
const brandMuted = '#7a9490'
const brandBorder = '#d4ede6'
const amber = '#f59e0b'
const red = '#e53e3e'

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  centerWrap: {
    flex: 1,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    px: 2,
    py: { xs: 3, sm: 5 },
  },
  card: {
    width: '100%',
    maxWidth: 520,
    bgcolor: '#fff',
    borderRadius: '20px',
    boxShadow: [
      '0 1px 3px rgba(0,0,0,.05)',
      '0 8px 32px rgba(46,204,154,.08)',
      `0 0 0 1px ${brandBorder}`,
    ].join(', '),
    paddingTop: 1,
    overflow: 'hidden',
  },
  cardHeader: {
    background: `linear-gradient(135deg, ${brandGreenDark} 0%, ${brandGreen} 100%)`,
    px: { xs: 2.5, sm: 4 },
    py: 2.75,
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
  },
  brandLogoWrap: {
    width: 32,
    height: 32,
    borderRadius: '8px',
    bgcolor: 'rgba(255,255,255,.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  brandLogoImg: {
    maxWidth: 26,
    maxHeight: 26,
    objectFit: 'contain',
  },
  brandName: {
    fontSize: 17,
    fontWeight: 600,
    color: '#fff',
    letterSpacing: '-0.01em',
  },
  brandBadge: {
    ml: 'auto',
    fontSize: 11,
    fontWeight: 500,
    color: 'rgba(255,255,255,.75)',
    bgcolor: 'rgba(255,255,255,.15)',
    borderRadius: '100px',
    px: 1.25,
    py: 0.375,
    letterSpacing: '0.03em',
    whiteSpace: 'nowrap',
  },
  cardBody: {
    px: { xs: 2.5, sm: 4 },
    pt: 4.5,
    pb: 4,
  },
  iconCluster: {
    display: 'flex',
    justifyContent: 'center',
    mb: 3.5,
    height: 72,
    position: 'relative',
  },
  iconCircle: {
    width: 64,
    height: 64,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  iconError: {
    bgcolor: '#fff1f1',
    boxShadow: '0 0 0 8px #ffe4e4',
  },
  iconWarning: {
    bgcolor: '#fffbeb',
    boxShadow: '0 0 0 8px #fef3c7',
  },
  errorIcon: {
    fontSize: 32,
    color: red,
  },
  warningIcon: {
    fontSize: 32,
    color: amber,
  },
  azureBadge: {
    position: 'absolute',
    bottom: -4,
    right: -4,
    width: 26,
    height: 26,
    borderRadius: '50%',
    bgcolor: '#fff',
    boxShadow: '0 2px 8px rgba(0,0,0,.12)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  azureLogo: {
    width: 16,
    height: 16,
    borderRadius: '3px',
    background: 'linear-gradient(135deg, #0078d4 0%, #50e6ff 100%)',
  },
  errorTitle: {
    fontSize: { xs: '1.15rem', sm: '1.35rem' },
    fontWeight: 600,
    color: brandDark,
    textAlign: 'center',
    letterSpacing: '-0.02em',
    mb: 1.25,
    lineHeight: 1.25,
  },
  errorSubtitle: {
    fontSize: 14,
    color: brandMuted,
    textAlign: 'center',
    lineHeight: 1.6,
    maxWidth: 360,
    mx: 'auto',
    mb: 3.5,
  },
  emailDiff: {
    bgcolor: '#f4fbf8',
    border: `1px solid ${brandBorder}`,
    borderRadius: '12px',
    p: 2,
    mb: 3,
  },
  emailDiffLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: brandMuted,
    mb: 1.25,
  },
  emailRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    py: 1.125,
    px: 1.5,
    borderRadius: '8px',
    fontSize: 13,
    mb: 0.75,
    '&:last-of-type': { mb: 0 },
  },
  emailRowActive: {
    bgcolor: '#e6faf4',
    color: '#1a7a5a',
  },
  emailRowMismatch: {
    bgcolor: '#fff1f1',
    color: '#b91c1c',
  },
  emailDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
  },
  emailDotActive: { bgcolor: brandGreen },
  emailDotMismatch: { bgcolor: red },
  emailMono: {
    fontFamily: 'ui-monospace, monospace',
    fontWeight: 500,
    fontSize: 13,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    flex: 1,
    minWidth: 0,
  },
  emailTag: {
    ml: 'auto',
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    px: 1,
    py: 0.25,
    borderRadius: '100px',
    flexShrink: 0,
  },
  emailTagActive: {
    bgcolor: 'rgba(46,204,154,.2)',
    color: '#1a7a5a',
  },
  emailTagMismatch: {
    bgcolor: 'rgba(229,62,62,.15)',
    color: '#b91c1c',
  },
  detailBox: {
    borderRadius: '12px',
    p: 2,
    mb: 3,
  },
  detailError: {
    bgcolor: '#fff5f5',
    border: '1px solid #fecaca',
  },
  detailWarning: {
    bgcolor: '#fffbeb',
    border: '1px solid #fde68a',
  },
  detailRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1.25,
    fontSize: 13,
    color: brandText,
    lineHeight: 1.55,
    '& + &': {
      mt: 1.25,
      pt: 1.25,
      borderTop: '1px dashed rgba(0,0,0,.06)',
    },
  },
  detailDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    mt: '5px',
    flexShrink: 0,
  },
  detailDotErr: { bgcolor: red },
  detailDotWarn: { bgcolor: amber },
  detailText: { fontSize: 13, color: brandText, lineHeight: 1.55 },
  codeBlock: {
    bgcolor: brandDark,
    borderRadius: '10px',
    px: 2,
    py: 1.5,
    mb: 3,
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    flexWrap: 'wrap',
  },
  codeMuted: {
    fontFamily: 'ui-monospace, monospace',
    fontSize: 12,
    color: '#5a7a73',
  },
  codeValue: {
    fontFamily: 'ui-monospace, monospace',
    fontSize: 12,
    color: '#7be5c5',
    wordBreak: 'break-all',
  },
  actionRow: {
    display: 'flex',
    gap: 1.25,
    flexWrap: 'wrap',
  },
  btnPrimary: {
    flex: 1,
    minWidth: 130,
    py: 1.625,
    borderRadius: '10px',
    fontSize: 14,
    fontWeight: 500,
    textTransform: 'none',
    gap: 1,
    color: '#fff',
    background: `linear-gradient(135deg, ${brandGreenDark}, ${brandGreen})`,
    boxShadow: '0 4px 14px rgba(46,204,154,.35)',
    '&:hover': {
      background: `linear-gradient(135deg, ${brandGreenDark}, ${brandGreen})`,
      boxShadow: '0 6px 20px rgba(46,204,154,.45)',
    },
  },
  btnSecondary: {
    flex: 1,
    minWidth: 130,
    py: 1.625,
    gap: 1,

    borderRadius: '10px',
    fontSize: 14,
    fontWeight: 500,
    textTransform: 'none',
    color: brandText,
    bgcolor: '#f4fbf8',
    border: `1px solid ${brandBorder}`,
    '&:hover': {
      bgcolor: '#e6faf4',
      border: `1px solid ${brandBorder}`,
    },
  },
}

export default SamlErrorPage
